import { useEffect, useMemo, useState } from 'react';

/**
 * The code step's resend wait, kept so the number step can read it: what `carriedDeadline` carries back
 * when the same number is entered again.
 *
 * Neither surface keeps one hook across the two steps, so the wait cannot live in either step's state.
 * `PhoneVerifyModal` unmounts the code step's hook on "use a different number", and
 * `PostPurchasePhoneGate` keeps one hook but zeroes its deadline as the code step is left. Each FLOW owns
 * one carry instead (`useCodeWaitCarry`) and hands it to both of its steps. Both phones keep the same three
 * facts on their store (`phoneResendDeadline`, `phoneResendWaitSeconds`, `phoneResendWaitNumber`).
 */
export interface CodeWait {
  /** The E.164 number the code went to. */
  phone: string;
  /** When the wait runs out, in epoch millis. */
  until: number;
  /** The length the wait was armed with, for the ring. */
  seconds: number;
}

export interface CodeWaitCarry {
  /** The wait the code step last armed, or null. Read by the number step. */
  wait: CodeWait | null;
  /** Written by the code step each time it arms a wait. Stable across renders. */
  remember: (wait: CodeWait) => void;
}

/**
 * One flow's carry, emptied whenever that flow is not open.
 *
 * Owned by the flow rather than shared by the module, because the dialog and the post-purchase takeover
 * can both be mounted: a shared one let either flow overwrite or clear the other's wait. Emptied on `open`
 * going false, not inside any one close handler, because a flow can end without one: browser Back or a
 * navigation drops the dialog's `?verify=` and the dialog simply stops rendering. The next opening then
 * starts with nothing blocking a send, which is the phones' `clearPhoneSendWaits` rule.
 *
 * State rather than a ref, so the number step re-renders when the wait changes instead of relying on
 * nothing writing it while that step is mounted.
 */
export function useCodeWaitCarry(open: boolean): CodeWaitCarry {
  const [wait, setWait] = useState<CodeWait | null>(null);
  useEffect(() => {
    if (!open) setWait(null);
  }, [open]);
  return useMemo(() => ({ wait, remember: setWait }), [wait]);
}
